import { useState, useRef, useEffect } from 'react';
import { FaMicrophone, FaDownload, FaImage, FaSpinner, FaFileAlt, FaLanguage } from 'react-icons/fa';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import Constants from '../../utilities/constants/Constants';
import { extractTextFromImage } from '../../utilities/extensions/imageToText';
import { LanguageCodes, VoiceGenders } from '../../utilities/extensions/textToSpeech';

const AdminTools = () => {
  // Text to Audio state
  const [textInput, setTextInput] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(Constants.LANGUAGES.KANNADA);
  const [selectedVoiceGender, setSelectedVoiceGender] = useState(VoiceGenders.FEMALE);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState(''); // Storage path
  const [audioBlobUrl, setAudioBlobUrl] = useState(''); // Blob URL for playback
  const [audioError, setAudioError] = useState('');

  // Image to Text state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [imageError, setImageError] = useState('');
  const fileInputRef = useRef(null);

  const storage = getStorage();

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (audioBlobUrl) {
        URL.revokeObjectURL(audioBlobUrl);
      }
    };
  }, [audioBlobUrl]);

  // Text to Audio handlers
  const handleGenerateAudio = async () => {
    if (!textInput.trim()) {
      setAudioError('Please enter some text');
      return;
    }

    setIsGeneratingAudio(true);
    setAudioError('');
    setAudioUrl('');
    setAudioBlobUrl('');

    try {
      // Get the language code for TTS
      const languageCode = Constants.LANGUAGE_SHORT_CODES[selectedLanguage] || LanguageCodes.KANNADA;
      
      // Create document in audioConversions collection
      const audioConversionsCollection = collection(db, 'audioConversions');
      const docData = {
        languageCode: languageCode,
        ssmlGender: selectedVoiceGender,
        text: textInput.trim(),
        createdAt: new Date(),
        status: 'pending'
      };
      
      const docRef = await addDoc(audioConversionsCollection, docData);
      
      // Wait for audio to be generated (poll the document)
      const maxRetries = 10;
      const retryDelay = 2000;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Check if audioPath is available
          const audioPath = data.audioPath || `audio_conversions/${docRef.id}.mp3`;
          const audioRef = storageRef(storage, audioPath);
          
          try {
            // Try to get download URL - this will fail if file doesn't exist
            const downloadURL = await getDownloadURL(audioRef);
            // If successful, set both the download URL and path
            setAudioBlobUrl(downloadURL); // Use download URL for playback
            setAudioUrl(audioPath); // Store path for download
            return;
          } catch (storageError) {
            // File doesn't exist yet, continue retrying
            if (attempt === maxRetries) {
              throw new Error('Audio generation timed out');
            }
          }
        }
      }
      
      setAudioError('Failed to generate audio. Please try again.');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating audio:', error);
      setAudioError('Error generating audio: ' + (error.message || 'Unknown error'));
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleDownloadAudio = async () => {
    if (!audioUrl) return;

    try {
      // Get the download URL from Firebase Storage
      const audioRef = storageRef(storage, audioUrl);
      const downloadURL = await getDownloadURL(audioRef);
      
      // Use a simple anchor tag approach to download
      // This bypasses CORS issues by letting the browser handle the download
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = `audio_${selectedLanguage}_${Date.now()}.mp3`;
      link.target = '_blank'; // Open in new tab as fallback
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download error:', error);
      setAudioError('Failed to download audio: ' + error.message);
    }
  };

  // Image to Text handlers
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setImageError('Please select a valid image file');
        return;
      }

      setSelectedImage(file);
      setImageError('');
      setExtractedText('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExtractText = async () => {
    if (!selectedImage) {
      setImageError('Please select an image first');
      return;
    }

    setIsProcessingImage(true);
    setImageError('');
    setExtractedText('');

    try {
      // Extract text from image using the imageToText utility
      // This handles upload, processing, and cleanup automatically
      const text = await extractTextFromImage(
        selectedImage,
        storage,
        db,
        3000 // waitTimeMs - wait 3 seconds for processing
      );

      if (text && text.trim()) {
        setExtractedText(text);
      } else {
        setImageError('No text found in the image');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error extracting text:', error);
      setImageError('Error extracting text: ' + (error.message || 'Unknown error'));
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleCopyText = () => {
    if (extractedText) {
      navigator.clipboard.writeText(extractedText);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Admin Tools</h2>
        <p className="text-gray-600 mt-1">Utilities for content creation and management</p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Text to Audio Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <FaMicrophone className="text-blue-600 text-xl" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Text to Audio</h3>
            <p className="text-sm text-gray-600">Generate audio from text in different languages</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Language Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaLanguage className="inline mr-2" />
                Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.entries(Constants.LANGUAGE_NAMES).map(([key, name]) => (
                  <option key={key} value={key}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voice Gender
              </label>
              <select
                value={selectedVoiceGender}
                onChange={(e) => setSelectedVoiceGender(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={VoiceGenders.FEMALE}>Female</option>
                <option value={VoiceGenders.MALE}>Male</option>
                <option value={VoiceGenders.NEUTRAL}>Neutral</option>
              </select>
            </div>
          </div>

          {/* Text Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Text
            </label>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Enter text to convert to audio..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerateAudio}
            disabled={!textInput.trim() || isGeneratingAudio}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isGeneratingAudio ? (
              <>
                <FaSpinner className="animate-spin" />
                Generating Audio...
              </>
            ) : (
              <>
                <FaMicrophone />
                Generate Audio
              </>
            )}
          </button>

          {/* Audio Player and Download */}
          {audioBlobUrl && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm font-medium text-green-800 mb-2">Audio generated successfully!</p>
              <div className="flex items-center gap-2">
                <audio controls className="flex-1" key={audioBlobUrl}>
                  <source src={audioBlobUrl} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
                <button
                  onClick={handleDownloadAudio}
                  className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0"
                  title="Download Audio"
                >
                  <FaDownload className="text-lg" />
                </button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {audioError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{audioError}</p>
            </div>
          )}
        </div>
      </div>

      {/* Image to Text Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <FaImage className="text-purple-600 text-xl" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Image to Text</h3>
            <p className="text-sm text-gray-600">Extract text from images using OCR</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Image
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-purple-600"
            >
              <FaImage />
              {selectedImage ? selectedImage.name : 'Click to select image'}
            </button>
          </div>

          {/* Image Preview */}
          {imagePreview && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
              <img
                src={imagePreview}
                alt="Preview"
                className="max-w-full max-h-64 mx-auto rounded-lg"
              />
            </div>
          )}

          {/* Extract Button */}
          <button
            onClick={handleExtractText}
            disabled={!selectedImage || isProcessingImage}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessingImage ? (
              <>
                <FaSpinner className="animate-spin" />
                Processing Image...
              </>
            ) : (
              <>
                <FaFileAlt />
                Extract Text
              </>
            )}
          </button>

          {/* Extracted Text */}
          {extractedText && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-green-800">Extracted Text:</p>
                <button
                  onClick={handleCopyText}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                >
                  Copy
                </button>
              </div>
              <div className="bg-white rounded p-3 max-h-64 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-800">{extractedText}</pre>
              </div>
            </div>
          )}

          {/* Error Message */}
          {imageError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{imageError}</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminTools;
